import {
  Component,
  createEffect,
  createMemo,
  createResource,
  createSignal,
  For,
  onCleanup,
  Show,
} from "solid-js";
import { Portal } from "solid-js/web";
import { useNavigate } from "@solidjs/router";
import { Badge, Kbd } from "../ui";
import { TypeIcon } from "../work-items/TypeIcon";
import { resolveReferences, searchProject } from "../../api";
import type { ProjectId } from "../../api";
import { parseReferences } from "../../utils/references";
import {
  commandPaletteOpen,
  setCommandPaletteOpen,
  paletteQuery as query,
  setPaletteQuery as setQuery,
} from "../../stores/commandPaletteStore";
import { recentsFor } from "../../stores/recentsStore";
import { setQuickCreateOpen } from "../../stores/quickCreateStore";
import { useChatCommand } from "../../contexts/ChatCommandContext";
import {
  buildEmptyStateRows,
  buildPaletteRows,
  classifyQuery,
  defaultSelectedIndex,
  type PaletteRow,
} from "../../utils/paletteResults";
import { humanise, statusCategoryToVariant } from "../../utils/status";
import { canCreateProject } from "../../utils/permissions";
import { tenantRole } from "../../stores/currentUserStore";

export const PALETTE_PLACEHOLDER = "Search work items, docs, or ask AI...";
export const PALETTE_DEBOUNCE_MS = 175;

export const SearchIcon: Component<{ class?: string }> = (props) => (
  <svg
    class={props.class ?? "w-4 h-4"}
    fill="none"
    stroke="currentColor"
    viewBox="0 0 24 24"
    aria-hidden="true"
  >
    <path
      stroke-linecap="round"
      stroke-linejoin="round"
      stroke-width="2"
      d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
    />
  </svg>
);

const DocumentIcon: Component<{ class?: string }> = (props) => (
  <svg
    class={props.class ?? "w-[15px] h-[15px]"}
    fill="none"
    stroke="currentColor"
    viewBox="0 0 24 24"
    aria-hidden="true"
  >
    <path
      stroke-linecap="round"
      stroke-linejoin="round"
      stroke-width="2"
      d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
    />
  </svg>
);

/** DOM id for a row's `<li role="option">`, referenced by `aria-activedescendant`. */
function optionId(row: PaletteRow): string {
  return `palette-option-${row.kind}-${row.id}`;
}

/** Group heading text for a row; the AI row has no group. */
function groupOf(row: PaletteRow): string {
  return row.kind === "ai" ? "" : row.group;
}

/**
 * Whether `rows[i]` is the first row of a new group — the AI row never
 * starts one, and never counts as "the previous row" for this check either:
 * the pinned direct hit sits before the AI row and shares its group with
 * later Work Items rows, so this compares against the last preceding
 * *non-AI* row rather than the literal predecessor.
 */
function isGroupStart(rows: PaletteRow[], i: number): boolean {
  const row = rows[i];
  if (row.kind === "ai") return false;
  for (let j = i - 1; j >= 0; j--) {
    const prev = rows[j];
    if (prev.kind === "ai") continue;
    return prev.group !== row.group;
  }
  return true;
}

export interface CommandPaletteProps {
  projectId?: ProjectId;
  projectPrefix?: string;
}

/**
 * Command palette shell (FLOW-798) plus grouped results, the Ask-AI row and
 * default selection (FLOW-799). Keyboard navigation between rows and the
 * Ask-AI hand-off itself are FLOW-801; query debouncing is FLOW-803.
 *
 * Not built on `Modal`: that primitive traps Tab (FLOW-801 gives Tab to
 * "send to AI") and closes on every Escape, whereas the palette clears its
 * query on the first Escape and closes on the second.
 *
 * That two-stage Escape itself lives in the shell's document-level capture
 * handler, not here (FLOW-858): bound only to this component's input, it did
 * nothing at all once focus left the input (not even the first-press clear).
 * The shell handler runs ahead of any focus position and reads/clears
 * `query` directly from the store, so this component takes no Escape branch
 * of its own — see `shellShortcuts.ts` for the logic and `AppShell.tsx` for
 * why capture-phase registration is what makes it always run first.
 */
export const CommandPalette: Component<CommandPaletteProps> = (props) => {
  const navigate = useNavigate();
  const { requestChat } = useChatCommand();
  let inputRef: HTMLInputElement | undefined;
  let previouslyFocused: Element | null = null;

  // FLOW-803: only the server query is debounced (D1). Clearing the query or
  // closing the palette resets immediately and `onCleanup` kills the pending
  // timer, so an abandoned query never reaches the network (D2).
  const [debouncedQuery, setDebouncedQuery] = createSignal("");
  createEffect(() => {
    const q = query().trim();
    if (!commandPaletteOpen() || !q) {
      setDebouncedQuery("");
      return;
    }
    const timer = setTimeout(() => setDebouncedQuery(q), PALETTE_DEBOUNCE_MS);
    onCleanup(() => clearTimeout(timer));
  });
  const searchPending = () => query().trim() !== debouncedQuery();

  // No project selected, or nothing typed: nothing to ask the server. A
  // `false` source means `createResource` never fetches — this is also the
  // guarantee behind Review Focus 5 (no project selected still renders the
  // AI row, with zero requests fired).
  const [results] = createResource(
    () => {
      const pid = props.projectId;
      const q = debouncedQuery();
      return pid && q ? ([pid, q] as const) : false;
    },
    ([pid, q]) => searchProject(pid, q),
  );

  // Recently visited items for the empty state. Gated on `commandPaletteOpen()`
  // even though the whole component is already wrapped in `<Show when=
  // {commandPaletteOpen()}>` below: the component body itself runs once at
  // mount, before that Show ever gates anything, so without this gate the
  // resource would fire on every mount regardless of whether the palette is
  // actually open.
  const recents = createMemo(() => recentsFor(props.projectId));
  const [resolved] = createResource(
    () => {
      const pid = props.projectId;
      const ids = recents();
      return commandPaletteOpen() && !query().trim() && pid && ids.length
        ? ([pid, ids] as const)
        : false;
    },
    ([pid, ids]) =>
      resolveReferences(pid, {
        work_item_display_ids: [],
        work_item_ids: ids
          .filter((e) => e.kind === "work_item")
          .map((e) => e.id),
        document_ids: ids.filter((e) => e.kind === "document").map((e) => e.id),
      }),
  );

  const intent = createMemo(() => classifyQuery(query(), props.projectPrefix));
  const rows = createMemo(() => {
    if (!query().trim())
      return buildEmptyStateRows(
        resolved.error ? undefined : resolved(),
        recents(),
        Boolean(props.projectId),
        canCreateProject(tenantRole()),
      );
    // `createResource` keeps its last-resolved value when the source goes
    // falsy (Solid's loadEnd carries `value` through on a `false` source),
    // so a project deselected mid-search would otherwise keep rendering the
    // previous project's rows even though no fetch is in flight. Gate on
    // the current projectId, not just on `results()`. `results.error` is
    // read (not `results()`) to check failure — calling the resource
    // accessor itself while errored rethrows the rejection (Solid's
    // createResource `read()`), and this palette portals outside any
    // ErrorBoundary.
    const data = props.projectId && !results.error ? results() : undefined;
    return buildPaletteRows(query(), props.projectPrefix, data);
  });
  const [selected, setSelected] = createSignal(0);
  createEffect(() => setSelected(defaultSelectedIndex(rows(), intent())));
  const activeDescendant = createMemo(() => {
    const row = rows()[selected()];
    return row ? optionId(row) : undefined;
  });

  // The listbox scrolls at max-h-[50vh]; keep the selected row in view. By id
  // rather than a ref array — optionId() already labels every row for
  // aria-activedescendant, and the palette portals to document.body.
  createEffect(() => {
    const id = activeDescendant();
    if (id) document.getElementById(id)?.scrollIntoView({ block: "nearest" });
  });

  const close = () => setCommandPaletteOpen(false);

  const move = (delta: number) => {
    const n = rows().length;
    if (n === 0) return;
    setSelected((i) => (i + delta + n) % n);
  };

  // Hand the query to chat, framed by whatever single work-item reference it
  // names (FLOW-802). With no project selected neither ChatDrawer nor
  // GlobalQuickCreate is mounted (AppShell gates both on the project), so a
  // command queued here would never be consumed — do nothing and leave the
  // palette open rather than closing it on a no-op.
  const askAi = () => {
    const q = query().trim();
    const pid = props.projectId;
    if (!q || !pid) return;
    close();

    // Capture everything the network call needs before the first await —
    // `query()`/`props.projectId` may change under a stale read otherwise
    // (FLOW-814). The captured `pid` is also compared against the live
    // `props.projectId` once the resolve settles: a project switch during
    // that round trip must drop the command rather than let `requestChat`
    // re-stamp it under the new project (FLOW-829). Zero or multiple
    // references: send the plain ask with no resolve call at all. Documents
    // are out of scope (D5).
    const refs = parseReferences(q, props.projectPrefix ?? "").filter(
      (ref) => ref.kind === "work_item",
    );
    if (refs.length !== 1) {
      requestChat(q);
      return;
    }
    const ref = refs[0];

    resolveReferences(pid, {
      work_item_display_ids: ref.displayId ? [ref.displayId] : [],
      work_item_ids: ref.id ? [ref.id] : [],
      document_ids: [],
    })
      .catch(() => undefined)
      .then((result) => {
        if (props.projectId !== pid) return;
        const item = result?.work_items[0];
        requestChat(
          q,
          item ? { type: "work_item", workItemId: item.id } : undefined,
        );
      });
  };

  // `close()` runs first everywhere: the palette restores focus to whatever
  // was focused before it opened, and that must happen before a route change
  // or the quick-create modal's own autofocus.
  const activate = (row: PaletteRow | undefined) => {
    if (!row) return;
    if (row.kind === "ai") return askAi();
    if (row.kind === "action" && row.command === "new-work-item") {
      if (!props.projectId) return;
      close();
      setQuickCreateOpen(true);
      return;
    }
    if (!("href" in row) || !row.href) return;
    close();
    navigate(row.href);
  };

  const handleInputKeyDown = (e: KeyboardEvent) => {
    if (e.key === "ArrowDown" || e.key === "ArrowUp") {
      e.preventDefault();
      move(e.key === "ArrowDown" ? 1 : -1);
      return;
    }
    if (e.key === "Enter") {
      e.preventDefault();
      if (e.metaKey || e.ctrlKey) return askAi();
      return activate(rows()[selected()]);
    }
    // Tab is only taken when it would otherwise do nothing useful: the
    // palette has no focus trap by design (FLOW-798), so Shift+Tab and an
    // empty query keep the browser's own focus movement.
    if (e.key === "Tab" && !e.shiftKey && query().trim() && props.projectId) {
      e.preventDefault();
      return askAi();
    }
    // Escape is not handled here — the shell's capture-phase handler owns it
    // (FLOW-858) so the clear-then-close works regardless of focus.
  };

  const handleBackdropClick = (e: MouseEvent) => {
    if (e.target === e.currentTarget) close();
  };

  // Mirrors Modal.tsx: capture focus and lock scroll on open, undo on close.
  // The reset-on-open here is load-bearing now, not incidental: `query` is
  // module-level state in commandPaletteStore (FLOW-858), shared with the
  // shell handler, so without this reset a query left over from the last
  // time the palette was open (e.g. closed via the shell's second Escape
  // press) would still be sitting there the next time it opens.
  createEffect(() => {
    if (!commandPaletteOpen()) return;
    previouslyFocused = document.activeElement;
    setQuery("");
    document.body.style.overflow = "hidden";
    requestAnimationFrame(() => inputRef?.focus());
    onCleanup(() => {
      document.body.style.overflow = "";
      if (
        previouslyFocused instanceof HTMLElement &&
        document.contains(previouslyFocused)
      ) {
        previouslyFocused.focus();
      }
      previouslyFocused = null;
    });
  });

  return (
    <Show when={commandPaletteOpen()}>
      <Portal mount={document.body}>
        <div
          class="fixed inset-0 z-50 flex justify-center items-stretch md:items-start md:p-4 md:pt-[12vh] bg-scrim/40 backdrop-blur-sm animate-fade-in"
          onClick={handleBackdropClick}
          role="presentation"
        >
          <div
            role="dialog"
            aria-modal="true"
            aria-label="Command palette"
            data-command-palette
            class="w-full md:max-w-xl md:h-auto bg-surface flex flex-col md:border md:border-line md:rounded-md md:shadow-overlay md:animate-scale-in"
          >
            <div class="flex items-center gap-2 h-12 px-3 border-b border-line">
              <SearchIcon class="w-4 h-4 text-ink-3 shrink-0" />
              <input
                ref={inputRef}
                type="text"
                role="combobox"
                class="flex-1 min-w-0 h-full bg-transparent text-sm text-ink placeholder:text-ink-3 outline-none"
                placeholder={PALETTE_PLACEHOLDER}
                aria-label="Search"
                aria-expanded={rows().length > 0}
                aria-controls={
                  rows().length > 0 ? "palette-results" : undefined
                }
                aria-activedescendant={activeDescendant()}
                autocomplete="off"
                spellcheck={false}
                value={query()}
                onInput={(e) => setQuery(e.currentTarget.value)}
                onKeyDown={handleInputKeyDown}
              />
              <Kbd aria-hidden="true" class="hidden md:inline-flex">
                esc
              </Kbd>
              <button
                type="button"
                onClick={close}
                aria-label="Close search"
                class="md:hidden p-1.5 -mr-1.5 text-ink-3 hover:text-ink rounded-sm transition-colors focus-ring"
              >
                <svg
                  class="w-4 h-4"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                  aria-hidden="true"
                >
                  <path
                    stroke-linecap="round"
                    stroke-linejoin="round"
                    stroke-width="2"
                    d="M6 18L18 6M6 6l12 12"
                  />
                </svg>
              </button>
            </div>

            <Show when={rows().length > 0}>
              <ul
                id="palette-results"
                role="listbox"
                aria-label="Search results"
                class="flex-1 md:max-h-[50vh] overflow-y-auto py-1"
              >
                <For each={rows()}>
                  {(row, i) => {
                    const workItemRow = () =>
                      row.kind === "work_item" ? row : undefined;
                    const documentRow = () =>
                      row.kind === "document" ? row : undefined;
                    const decisionRow = () =>
                      row.kind === "decision" ? row : undefined;
                    const aiRow = () => (row.kind === "ai" ? row : undefined);
                    const actionRow = () =>
                      row.kind === "action" ? row : undefined;
                    return (
                      <>
                        <Show when={isGroupStart(rows(), i())}>
                          <li
                            role="presentation"
                            class="px-3 pt-2 pb-1 text-[10px] font-medium uppercase tracking-[0.12em] text-ink-3"
                          >
                            {groupOf(row)}
                          </li>
                        </Show>
                        <li
                          id={optionId(row)}
                          role="option"
                          aria-selected={selected() === i()}
                          data-palette-row={row.kind}
                          onClick={() => activate(row)}
                          onMouseMove={() => setSelected(i())}
                          class={
                            "flex items-center gap-2 px-3 py-1.5 text-sm cursor-pointer " +
                            (selected() === i()
                              ? "bg-sunken text-ink"
                              : "text-ink-2")
                          }
                        >
                          <Show when={workItemRow()}>
                            {(r) => (
                              <>
                                <TypeIcon typeName={r().item.item_type} />
                                <Show when={r().pinned}>
                                  <span class="text-ink-3">Go to</span>
                                </Show>
                                <span class="font-mono text-ink-3 shrink-0">
                                  {r().item.display_id}
                                </span>
                                <span class="truncate">{r().item.title}</span>
                                <Badge
                                  variant={
                                    statusCategoryToVariant[
                                      r().item.status_category
                                    ]
                                  }
                                >
                                  {r().item.status_name}
                                </Badge>
                              </>
                            )}
                          </Show>
                          <Show when={documentRow()}>
                            {(r) => (
                              <>
                                <DocumentIcon class="w-[15px] h-[15px] text-ink-3 shrink-0" />
                                <span class="truncate">{r().doc.title}</span>
                                <span class="text-ink-3 text-xs">
                                  {humanise(r().doc.doc_type)}
                                </span>
                              </>
                            )}
                          </Show>
                          <Show when={decisionRow()}>
                            {(r) => (
                              <>
                                <span class="truncate">
                                  {r().decision.title}
                                </span>
                                <span class="text-ink-3 text-xs">
                                  {humanise(r().decision.category)}
                                </span>
                                <Show when={r().decision.unconfirmed}>
                                  <Badge variant="inReview">Unconfirmed</Badge>
                                </Show>
                              </>
                            )}
                          </Show>
                          <Show when={aiRow()}>
                            {(r) => (
                              <>
                                <span class="font-mono text-[9px] uppercase tracking-[0.12em] px-1 rounded-sm border border-signal-magenta/25 text-signal-magenta">
                                  ai
                                </span>
                                <span class="truncate">{r().label}</span>
                              </>
                            )}
                          </Show>
                          <Show when={actionRow()}>
                            {(r) => <span class="truncate">{r().label}</span>}
                          </Show>
                        </li>
                      </>
                    );
                  }}
                </For>
              </ul>
            </Show>

            <Show
              when={
                props.projectId &&
                query().trim() &&
                !searchPending() &&
                !results.loading &&
                !results.error &&
                rows().length === 1
              }
            >
              <p class="px-3 py-2 text-sm text-ink-3">
                No items found for "{query().trim()}". Press Enter to ask AI.
              </p>
            </Show>
            <Show when={!searchPending() && results.error && !results.loading}>
              <p class="px-3 py-2 text-sm text-ink-3">
                Search is unavailable right now. Press Enter to ask AI.
              </p>
            </Show>
          </div>
        </div>
      </Portal>
    </Show>
  );
};
